import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  DamZoneDetailResponseSchema,
  DamZoneListResponseSchema,
  DamZoneRequestErrorSchema,
  DamZoneRequestResponseSchema,
  type DamZoneJob,
  type DamZoneListResponse,
  type DamZoneSummary,
} from "@chro/shared";
import { ApiError, apiContract } from "../../../lib/api";
import { cn } from "../../../lib/utils";
import { useAuth } from "../../../stores/auth";
import { ZoneMap, ZoneMapLegend, type ZoneMapTone } from "../../../components/eex/research/ZoneMap";
import {
  RESEARCH_POLL_MS,
  ZONE_GROUPS,
  flagTexts,
  formatDay,
  formatMonth,
  formatRange,
  futuresCheckText,
  gapReasonText,
  groupZones,
  isInProgress,
  isJobActive,
  jobEtaText,
  jobKindLabel,
  jobProgressText,
  mapSummary,
  requestBlockedText,
  requestErrorText,
  requestOutcomeText,
  resolutionText,
  validationText,
  zoneMapZones,
  zoneRequestAction,
  zoneStatusLabel,
  zoneTone,
} from "./dam-zones";

export const RESEARCH_ZONES_QUERY_KEY = ["eex-research", "dam-zones"] as const;
const zoneDetailKey = (key: string) => ["eex-research", "dam-zone", key] as const;

const LEGEND: readonly { tone: ZoneMapTone; label: string }[] = [
  { tone: "stored", label: "Stored" },
  { tone: "partial", label: "Partly stored" },
  { tone: "progress", label: "In progress" },
  { tone: "failed", label: "Failed" },
  { tone: "available", label: "Available to add" },
  { tone: "unavailable", label: "Not on ENTSO-E" },
];

interface Feedback {
  zoneKey: string;
  text: string;
  error: boolean;
}

function requestFailureText(error: unknown): string {
  if (error instanceof ApiError) {
    const parsed = DamZoneRequestErrorSchema.safeParse(error.body);
    if (parsed.success) return requestErrorText(parsed.data);
    if (error.status === 403) return "You do not have permission to add zones.";
  }
  return error instanceof Error ? error.message : "The request failed.";
}

const chipId = (key: string) => `research-chip-${key}`;

// EEX Research → Data: which day-ahead price zones CASCENA stores for everyone,
// and the way to add more. Zone focus is local to this view.
export function ResearchDataView() {
  const queryClient = useQueryClient();
  const zonesQ = useQuery({
    queryKey: RESEARCH_ZONES_QUERY_KEY,
    queryFn: () => apiContract(DamZoneListResponseSchema, "/eex/research/dam-zones"),
    refetchInterval: (query) => (query.state.data?.zones.some(isInProgress) ? RESEARCH_POLL_MS : false),
  });
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const request = useMutation({
    mutationFn: (zoneKey: string) => apiContract(DamZoneRequestResponseSchema, "/eex/research/dam-zones", {
      method: "POST",
      body: JSON.stringify({ zoneKey }),
    }),
    onMutate: () => setFeedback(null),
    onSuccess: (response) => {
      queryClient.setQueryData<DamZoneListResponse>(RESEARCH_ZONES_QUERY_KEY, (current) => current && {
        ...current,
        zones: current.zones.map((zone) => (zone.key === response.zone.key ? response.zone : zone)),
      });
      void queryClient.invalidateQueries({ queryKey: RESEARCH_ZONES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: zoneDetailKey(response.zone.key) });
      setFeedback({ zoneKey: response.zone.key, text: requestOutcomeText(response), error: false });
    },
    onError: (error, zoneKey) => setFeedback({ zoneKey, text: requestFailureText(error), error: true }),
  });

  if (!zonesQ.data) {
    if (!zonesQ.error) return <p role="status" className="research-empty">Loading zones…</p>;
    return (
      <p role="alert" className="research-empty text-negRed">
        Zones could not be loaded: {(zonesQ.error as Error).message}{" "}
        <button type="button" className="research-link" onClick={() => void zonesQ.refetch()}>Retry</button>
      </p>
    );
  }

  const data = zonesQ.data;
  const groups = groupZones(data.zones);
  const focused = data.zones.find((zone) => zone.key === focusKey) ?? null;
  const focusShape = (shapeId: string) => {
    const zone = data.zones.find((candidate) => candidate.mapShapeId === shapeId);
    if (zone) setFocusKey(zone.key);
  };

  return (
    <div className="research-data">
      {zonesQ.error && (
        <p role="alert" className="research-note research-refresh-error">
          Could not refresh zones ({(zonesQ.error as Error).message}). Showing the data from{" "}
          {new Date(data.generatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}.{" "}
          <button type="button" className="research-link" onClick={() => void zonesQ.refetch()}>Retry</button>
        </p>
      )}
      <div className="research-data-main">
        <div className="research-card research-map-card">
          <div className="research-map-toolbar">
            <ZoneMapLegend items={LEGEND} />
            <span className="research-updated">Updated {new Date(data.generatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <ZoneMap label={mapSummary(data.zones)} zones={zoneMapZones(data.zones)} activeShapeId={focused?.mapShapeId ?? null} onZoneSelect={focusShape} />
        </div>
        <div className="research-groups">
          {ZONE_GROUPS.map((group) => groups[group.id].length === 0 ? null : (
            <section key={group.id} className="research-group" aria-labelledby={`research-group-${group.id}`}>
              <h3 id={`research-group-${group.id}`}>{group.label} <span>{groups[group.id].length}</span></h3>
              <ul>
                {groups[group.id].map((zone) => (
                  <li key={zone.key}>
                    <button
                      id={chipId(zone.key)}
                      type="button"
                      className={cn("research-chip", `research-chip-${zoneTone(zone)}`)}
                      aria-pressed={zone.key === focusKey}
                      aria-label={`${zone.name}, ${zoneStatusLabel(zone)}${zone.job && isInProgress(zone) ? `, ${Math.floor(zone.job.percent)}%` : ""}`}
                      onClick={() => setFocusKey(zone.key === focusKey ? null : zone.key)}
                    >
                      <span className="research-chip-dot" aria-hidden="true" />
                      {zone.name}
                      {zone.job && isInProgress(zone) && <span className="research-chip-percent" aria-hidden="true">{Math.floor(zone.job.percent)}%</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
      <aside className="research-data-side" aria-label="Zone details">
        {groups.progress.length > 0 && (
          <section className="research-card" aria-labelledby="research-progress-title">
            <h2 id="research-progress-title" className="research-card-title">Downloads in progress</h2>
            <ul className="research-progress-list">
              {groups.progress.map((zone) => (
                <li key={zone.key}>
                  <button type="button" className="research-progress-zone" onClick={() => setFocusKey(zone.key)}>{zone.name}</button>
                  {zone.job && <JobProgress job={zone.job} zoneName={zone.name} compact />}
                </li>
              ))}
            </ul>
          </section>
        )}
        {focused
          ? <ZoneDetail
              key={focused.key}
              zone={focused}
              list={data}
              feedback={feedback?.zoneKey === focused.key ? feedback : null}
              pending={request.isPending && request.variables === focused.key}
              onRequest={() => request.mutate(focused.key)}
              onClose={() => {
                setFocusKey(null);
                document.getElementById(chipId(focused.key))?.focus();
              }}
            />
          : <Overview list={data} storedCount={groups.stored.length} />}
      </aside>
    </div>
  );
}

function useBlockedText(list: DamZoneListResponse): string | null {
  const role = useAuth((state) => state.user?.role);
  return requestBlockedText(list.requestBlockedReason, role);
}

function Overview({ list, storedCount }: { list: DamZoneListResponse; storedCount: number }) {
  const blocked = useBlockedText(list);
  return (
    <section className="research-card" aria-labelledby="research-overview-title">
      <h2 id="research-overview-title" className="research-card-title">Day-ahead prices</h2>
      <p>
        ENTSO-E day-ahead auction prices, stored once and shared by every company on CASCENA. History goes back to{" "}
        {formatDay(list.historyStart)}, or to when a zone began.
      </p>
      <p className="research-muted">
        {storedCount === 1 ? "1 zone is" : `${storedCount} zones are`} stored. Select a zone on the map or in the lists to see its
        coverage and checks{list.canRequest ? ", or to add it" : ""}.
      </p>
      {blocked && <p className="research-note">{blocked}</p>}
    </section>
  );
}

function JobProgress({ job, zoneName, compact = false }: { job: DamZoneJob; zoneName: string; compact?: boolean }) {
  if (!isJobActive(job)) return <p className={cn("research-job-stopped", `research-job-${job.phase}`)}>{jobProgressText(job)}</p>;
  const eta = jobEtaText(job);
  const percent = Math.floor(job.percent);
  return (
    <div className={cn("research-job", compact && "research-job-compact", `research-job-${job.phase}`)}>
      <div
        className="research-bar"
        role="progressbar"
        aria-label={`${zoneName} ${jobKindLabel(job).toLowerCase()}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`${percent}%, ${jobProgressText(job)}`}
      >
        <span style={{ width: `${Math.max(job.percent, 2)}%` }} />
      </div>
      <p className="research-job-text">
        <span>{jobProgressText(job)}</span>
        <span className="research-muted">{percent}%{eta ? ` · ${eta}` : ""}</span>
      </p>
      {!compact && job.requestedByYourCompany && <p className="research-muted">Requested by your company.</p>}
    </div>
  );
}

function ZoneDetail({ zone, list, feedback, pending, onRequest, onClose }: {
  zone: DamZoneSummary;
  list: DamZoneListResponse;
  feedback: Feedback | null;
  pending: boolean;
  onRequest: () => void;
  onClose: () => void;
}) {
  const detailQ = useQuery({
    // A new validation (for example after a gap retry) refetches the full gap list.
    queryKey: [...zoneDetailKey(zone.key), zone.validation?.validatedAt ?? null],
    queryFn: () => apiContract(DamZoneDetailResponseSchema, `/eex/research/dam-zones/${encodeURIComponent(zone.key)}`),
    enabled: zone.validation !== null,
  });
  const action = zoneRequestAction(zone);
  const blocked = useBlockedText(list);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // A request removes or disables its button; keep keyboard focus in the panel.
  useEffect(() => {
    if (feedback && (!document.activeElement || document.activeElement === document.body)) headingRef.current?.focus();
  }, [feedback]);
  const coverage = zone.coverage;
  const validation = zone.validation;
  const resolution = coverage ? resolutionText(coverage) : null;
  const gaps = detailQ.data?.gapRanges ?? validation?.gapRanges ?? [];
  const gapsTruncated = detailQ.data?.gapRangesTruncated ?? validation?.gapRangesTruncated ?? false;
  const mismatches = detailQ.data?.futuresMismatches ?? [];
  const flags = validation ? flagTexts(validation) : [];
  return (
    <section className="research-card research-detail" aria-labelledby="research-detail-title">
      <header className="research-detail-header">
        <div>
          <h2 id="research-detail-title" ref={headingRef} tabIndex={-1} className="research-card-title">{zone.name}</h2>
          <p className="research-muted">
            {zone.code}{zone.eexFamilies.length > 0 ? ` · EEX ${zone.eexFamilies.join(", ")}` : ""}
          </p>
        </div>
        <span className={cn("research-status", `research-status-${zoneTone(zone)}`)}>{zoneStatusLabel(zone)}</span>
        <button type="button" className="research-close" aria-label="Close zone details" onClick={onClose}>×</button>
      </header>

      {zone.unavailableReason && <p>{zone.unavailableReason}</p>}
      {zone.job && <JobProgress job={zone.job} zoneName={zone.name} />}

      {coverage && (
        <dl className="research-facts">
          <div><dt>Stored</dt><dd>{formatRange(coverage.firstDate, coverage.lastDate)}</dd></div>
          <div>
            <dt>Days</dt>
            <dd>
              {coverage.daysStored.toLocaleString("en-US")} stored
              {coverage.daysKnownGap > 0 && `, ${coverage.daysKnownGap.toLocaleString("en-US")} missing at ENTSO-E`}
              {coverage.daysPending > 0 && `, ${coverage.daysPending.toLocaleString("en-US")} awaiting publication`}
            </dd>
          </div>
          {resolution && <div><dt>Resolution</dt><dd>{resolution}</dd></div>}
          {!coverage.fullHistory && <div><dt>History</dt><dd>Not yet complete</dd></div>}
        </dl>
      )}

      {validation && (
        <div className="research-checks">
          <h3>Checks</h3>
          <p>
            {validationText(validation)}
            {validation.checked && <span className="research-muted"> {formatRange(validation.checked.from, validation.checked.to)}.</span>}
          </p>
          <p>{futuresCheckText(validation)}</p>
          {flags.length > 0 && <ul className="research-flags">{flags.map((text) => <li key={text}>{text}</li>)}</ul>}
          {gaps.length > 0 && (
            <details>
              <summary>Missing days ({gaps.length}{gapsTruncated ? "+" : ""})</summary>
              <ul className="research-gaps">
                {gaps.map((gap) => <li key={`${gap.from}-${gap.reason}`}>{formatRange(gap.from, gap.to)} <span className="research-muted">{gapReasonText(gap.reason)}</span></li>)}
              </ul>
              {gapsTruncated && <p className="research-muted">Only the first {gaps.length} ranges are listed.</p>}
            </details>
          )}
          {mismatches.length > 0 && (
            <details>
              <summary>Months that differ from the future ({mismatches.length})</summary>
              <table className="research-table">
                <thead><tr><th scope="col">Month</th><th scope="col">Final settlement</th><th scope="col">DAM average</th><th scope="col">Difference</th></tr></thead>
                <tbody>
                  {mismatches.map((row) => (
                    <tr key={`${row.family}-${row.month}`}>
                      <td>{formatMonth(row.month)}</td>
                      <td>{row.finalSettlementEurMwh.toFixed(2)}</td>
                      <td>{row.damAverageEurMwh.toFixed(2)}</td>
                      <td>{row.differenceEurMwh > 0 ? "+" : ""}{row.differenceEurMwh.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="research-muted">€/MWh. Informational only; it does not change the stored prices.</p>
            </details>
          )}
          {detailQ.error && <p role="alert" className="text-negRed">Details could not be loaded: {(detailQ.error as Error).message}</p>}
        </div>
      )}

      {zone.notes.length > 0 && <ul className="research-notes">{zone.notes.map((note) => <li key={note}>{note}</li>)}</ul>}

      {action && (list.canRequest
        ? (
          <div className="research-action">
            <p className="research-muted">{action.explanation}</p>
            <button type="button" className="research-primary" disabled={pending} onClick={onRequest}>
              {pending ? "Sending…" : action.label}
            </button>
          </div>
        )
        : blocked && <p className="research-note">{blocked}</p>)}
      {/* Mounted up front so screen readers announce the text when it arrives. */}
      <p role="status" className="research-live text-posGreen">{feedback && !feedback.error ? feedback.text : ""}</p>
      <p role="alert" className="research-live text-negRed">{feedback?.error ? feedback.text : ""}</p>
    </section>
  );
}
